import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import './Reports.css';

interface TopOffer {
  id: number;
  rank: number;
  offerName: string;
  advertiser: string;
  category: string;
  eventType: 'install' | 'lead' | 'sale' | 'event';
  payout: number;
  conversions: number;
  clicks: number;
  cvr: number;
  epc: number;
  revenue: number;
  profit: number;
  affiliatesCount: number;
  countries: string[];
  status: 'active' | 'paused' | 'private';
  featured: boolean;
  createdDate: string;
  lastActivity: string;
  trend: 'up' | 'down' | 'stable';
  trendPercent: number;
}

const TopOffers: React.FC = () => {
  const [periodFilter, setPeriodFilter] = useState<string>('7days');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [sortBy, setSortBy] = useState<string>('conversions');
  const [searchTerm, setSearchTerm] = useState('');
  const [topOffers, setTopOffers] = useState<TopOffer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTopOffers = async () => {
      try {
        setLoading(true);
        const response = await apiService.getTopOffers({
          period: periodFilter,
          limit: 50
        });
        setTopOffers(response.data.map((offer: any) => ({
          ...offer,
          cvr: parseFloat(offer.cvr),
          epc: parseFloat(offer.epc),
          revenue: parseFloat(offer.revenue),
          profit: parseFloat(offer.profit),
          countries: [],
          featured: false
        })));
      } catch (error: any) {
        console.error('Failed to fetch top offers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTopOffers();
  }, [periodFilter]);

  const filteredOffers = topOffers.filter(offer => {
    const searchMatch = offer.offerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       offer.advertiser.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       offer.category.toLowerCase().includes(searchTerm.toLowerCase());

    const categoryMatch = categoryFilter === 'all' || offer.category.toLowerCase().includes(categoryFilter.toLowerCase());
    const statusMatch = statusFilter === 'all' || offer.status === statusFilter;

    return searchMatch && categoryMatch && statusMatch;
  });

  const sortedOffers = [...filteredOffers].sort((a, b) => {
    switch (sortBy) {
      case 'conversions':
        return b.conversions - a.conversions;
      case 'cvr':
        return b.cvr - a.cvr;
      case 'epc':
        return b.epc - a.epc;
      case 'revenue':
        return b.revenue - a.revenue;
      case 'profit':
        return b.profit - a.profit;
      case 'affiliates':
        return b.affiliatesCount - a.affiliatesCount;
      default:
        return a.rank - b.rank;
    }
  });

  const calculateStats = () => {
    return {
      total: filteredOffers.length,
      featured: filteredOffers.filter(o => o.featured).length,
      active: filteredOffers.filter(o => o.status === 'active').length,
      totalConversions: filteredOffers.reduce((sum, o) => sum + o.conversions, 0),
      totalRevenue: filteredOffers.reduce((sum, o) => sum + o.revenue, 0),
      avgCvr: filteredOffers.reduce((sum, o) => sum + o.cvr, 0) / filteredOffers.length,
      avgEpc: filteredOffers.reduce((sum, o) => sum + o.epc, 0) / filteredOffers.length
    };
  };

  const stats = calculateStats();

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'private': return 'info';
      default: return 'secondary';
    }
  };

  const getEventTypeClass = (eventType: string) => {
    switch (eventType) {
      case 'install': return 'install';
      case 'lead': return 'lead';
      case 'sale': return 'sale';
      case 'event': return 'event';
      default: return 'default';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return '↗';
      case 'down': return '↘';
      case 'stable': return '→';
      default: return '';
    }
  };

  const getTrendClass = (trend: string) => {
    switch (trend) {
      case 'up': return 'profit';
      case 'down': return 'rejected';
      case 'stable': return '';
      default: return '';
    }
  };

  const exportData = (format: string) => {
    console.log(`Exporting top offers as ${format}`);
    alert(`Exporting ${filteredOffers.length} top offers as ${format}`);
  };

  const toggleFeatured = (id: number) => {
    console.log(`Toggling featured status for offer ${id}`);
  };

  return (
    <div className="reports-page">
      <div className="page-header">
        <h1>Top Offers</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Refresh Data
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="report-summary">
        <div className="summary-card">
          <h3>Total Offers</h3>
          <div className="value">{stats.total}</div>
        </div>
        <div className="summary-card">
          <h3>Featured Offers</h3>
          <div className="value approved">{stats.featured}</div>
        </div>
        <div className="summary-card">
          <h3>Active Offers</h3>
          <div className="value">{stats.active}</div>
        </div>
        <div className="summary-card">
          <h3>Total Conversions</h3>
          <div className="value">{stats.totalConversions.toLocaleString()}</div>
        </div>
        <div className="summary-card">
          <h3>Total Revenue</h3>
          <div className="value">${stats.totalRevenue.toFixed(2)}</div>
        </div>
        <div className="summary-card">
          <h3>Avg CVR</h3>
          <div className="value">{stats.avgCvr.toFixed(1)}%</div>
        </div>
        <div className="summary-card">
          <h3>Avg EPC</h3>
          <div className="value">${stats.avgEpc.toFixed(2)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="report-filters">
        <div className="filters-row">
          <div className="filter-group">
            <label>Period:</label>
            <select
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value)}
              className="group-select"
            >
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
              <option value="alltime">All Time</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Category:</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="group-select"
            >
              <option value="all">All Categories</option>
              <option value="social">Social Media</option>
              <option value="gaming">Gaming</option>
              <option value="finance">Finance</option>
              <option value="ecommerce">E-commerce</option>
              <option value="entertainment">Entertainment</option>
              <option value="education">Education</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="private">Private</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Sort By:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="group-select"
            >
              <option value="rank">Rank</option>
              <option value="conversions">Conversions</option>
              <option value="cvr">CVR</option>
              <option value="epc">EPC</option>
              <option value="revenue">Revenue</option>
              <option value="profit">Profit</option>
              <option value="affiliates">Affiliates</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Search:</label>
            <input
              type="text"
              placeholder="Search offers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="export-buttons">
          <button className="btn btn-success" onClick={() => exportData('CSV')}>
            CSV
          </button>
          <button className="btn btn-warning" onClick={() => exportData('Excel')}>
            Excel
          </button>
          <button className="btn btn-danger" onClick={() => exportData('PDF')}>
            PDF
          </button>
        </div>
      </div>

      {/* Top Offers Table */}
      <div className="report-content">
        <div className="table-container">
          <table className="report-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Offer</th>
                <th>Advertiser</th>
                <th>Category</th>
                <th>Event Type</th>
                <th>Payout</th>
                <th>Conversions</th>
                <th>CVR</th>
                <th>EPC</th>
                <th>Revenue</th>
                <th>Profit</th>
                <th>Affiliates</th>
                <th>Countries</th>
                <th>Trend</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedOffers.map((offer, index) => (
                <tr key={offer.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: index < 3 ? '#f39c12' : '#ecf0f1'
                      }}>
                        #{offer.rank}
                      </span>
                      {offer.featured && (
                        <span style={{
                          fontSize: '16px',
                          color: '#f39c12'
                        }}>
                          ⭐
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="offer-cell">
                      <span className="offer-name">{offer.offerName}</span>
                      <span className="offer-id">ID: {offer.id}</span>
                    </div>
                  </td>
                  <td>{offer.advertiser}</td>
                  <td>{offer.category}</td>
                  <td>
                    <span className={`event-type ${getEventTypeClass(offer.eventType)}`}>
                      {offer.eventType.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <span className="payout">${offer.payout.toFixed(2)}</span>
                  </td>
                  <td>
                    <span className="value">{offer.conversions.toLocaleString()}</span>
                  </td>
                  <td>
                    <span className="cvr">{offer.cvr.toFixed(1)}%</span>
                  </td>
                  <td>
                    <span className="epc">${offer.epc.toFixed(2)}</span>
                  </td>
                  <td>
                    <span className="revenue">${offer.revenue.toFixed(2)}</span>
                  </td>
                  <td>
                    <span className="profit">${offer.profit.toFixed(2)}</span>
                  </td>
                  <td>
                    <span className="value">{offer.affiliatesCount}</span>
                  </td>
                  <td>
                    <div style={{ fontSize: '12px', color: '#a0aec0' }}>
                      {offer.countries.slice(0, 3).join(', ')}
                      {offer.countries.length > 3 && ` +${offer.countries.length - 3}`}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ fontSize: '16px' }}>{getTrendIcon(offer.trend)}</span>
                      <span className={getTrendClass(offer.trend)}>
                        {offer.trendPercent > 0 ? '+' : ''}{offer.trendPercent.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(offer.status)}`}>
                      {offer.status.charAt(0).toUpperCase() + offer.status.slice(1)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn btn-info btn-sm">View</button>
                      <button
                        className={`btn btn-sm ${offer.featured ? 'btn-warning' : 'btn-success'}`}
                        onClick={() => toggleFeatured(offer.id)}
                      >
                        {offer.featured ? 'Unfeature' : 'Feature'}
                      </button>
                      <button className="btn btn-primary btn-sm">Edit</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="table-footer">
        <div className="table-info">
          Showing {filteredOffers.length} top offers for {periodFilter}
        </div>
      </div>
    </div>
  );
};

export default TopOffers;